import { HorizontalStepIndicator } from "@affinity/components/HorizontalStepIndicator";
import { Section } from "@affinity/components/Section";
import { Wrapper } from "@affinity/components/Wrapper/Wrapper";
import { breakpointWidth } from "@affinity/constants/StyleHelpers";
import QueryStringSetPlaceOrder from "@affinity/Handlers/QueryStringSetPlaceOrder";
import { BuildPersitenceUrl, getQueryStringFromState, getQueryStringFromUrl } from "@affinity/Store/PrePopulation";
import { CustomState } from "@affinity/Store/Reducers";
import { CreateAccountStep } from "@affinity/Store/StoreTypes";
import Step1PersonalDetails from "@affinity/Widgets/CreateAccount/Step1PersonalDetails";
import Step2Addresses from "@affinity/Widgets/CreateAccount/Step2Addresses";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import {
    ExcludeFromNavigation,
    ExcludeFromSignInRequired,
    HideFooter,
    HideHeader,
    MetaDescription,
    MetaKeywords,
    OpenGraphImage,
    OpenGraphTitle,
    OpenGraphUrl,
} from "@insite/client-framework/Types/FieldDefinition";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import Page from "@insite/mobius/Page";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import styled from "styled-components";

// Fields Keys Enumerator
const enum fields {
    categoryFilterId = "categoryFilterId",
}

// CMS Properties Types for Component Usage
interface OwnProps extends PageProps {
    fields: {
        [fields.categoryFilterId]: string;
        // Reference additional fields required here with relevant typings
    };
}

// Map Application State (Redux) to Component Properties
const mapStateToProps = (state: CustomState) => {
    return {
        isAuthenticated: state.context.session.isAuthenticated,
        currentStep: state.affinity_create_account.currentStep,
        redirectUrl: state.affinity_create_account.targetUrl,
        signInPageUrl: getPageLinkByPageType(state, "SignIn")?.url,
        queryStringUrl: getQueryStringFromUrl(state),
        placeOrderUrl: getPageLinkByPageType(state, "AffinityPlaceOrderPage")?.url,
        queryStringState: getQueryStringFromState(state),
        // Map state required to component properties with relevant typings
    };
};

// Bind Handlers to Component
const mapDispatchToProps = {
    // Reference handlers required
    QueryStringSetPlaceOrder
};

// Combine Properties
type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const stepsLabels = {
    [CreateAccountStep.First]: "Personal details",
    [CreateAccountStep.Second]: "Add address",
    [CreateAccountStep.Third]: "Preferences",
};

// Page Implementation
const AffinityCreateAccountPage: React.FC<Props> = ({
    id,
    redirectUrl,
    currentStep,
    isAuthenticated,
    signInPageUrl,
    queryStringUrl,
    QueryStringSetPlaceOrder,
    placeOrderUrl,
    queryStringState,
}) => {
    const { isInShell } = useShellContext();
    const history = useHistory();

    if (
        !isInShell &&
        isAuthenticated &&
        currentStep !== CreateAccountStep.Third &&
        currentStep !== CreateAccountStep.Complete
    ) {
        history.replace(signInPageUrl || redirectUrl || "/");
    }

    if (!isInShell) {
        React.useEffect(() => {
            if (currentStep === CreateAccountStep.Complete && redirectUrl) {
                // window.location.href = redirectUrl!;
                let targetUrl = redirectUrl!;
                if (queryStringState) {
                    window.location.href = BuildPersitenceUrl(placeOrderUrl!, queryStringState);
                } else {
                    history.push(targetUrl); // TODO: Confirm maintaining state does not cause an issue
                }
            }
        }, [currentStep]);
    }

    /**
     * Detect query string and set reducer
     * Used by dynamic button / links to restore query when linking / updating history
     */
    if (!isInShell) {
        React.useEffect(() => {
            if (queryStringUrl && queryStringUrl.page === "placeorder" && queryStringUrl.product) {
                QueryStringSetPlaceOrder(queryStringUrl);
            }
        }, []);
    }

    return (
        <Page hasCustomBackground>
            <PageGridContainer>
                <HeaderContainer>
                    <Zone contentId={id} zoneName="AffinityHeader" requireRows={false} />
                </HeaderContainer>
                <Section>
                    <HorizontalStepIndicator currentStep={currentStep} labels={stepsLabels} />
                    {(currentStep === CreateAccountStep.First || isInShell) && <Step1PersonalDetails />}
                    {(currentStep === CreateAccountStep.Second || isInShell) && <Step2Addresses />}
                    {(currentStep === CreateAccountStep.Third || isInShell) && (
                        <Zone contentId={id} zoneName="AffinityCreateAccountPageStep3" requireRows={false} />
                    )}
                </Section>
                <Zone contentId={id} zoneName="AffinitySidebar" requireRows={false} />
            </PageGridContainer>
        </Page>
    );
};

export const PageGridContainer = styled(Wrapper)`
    @media (min-width: ${breakpointWidth("lg")}) {
        display: grid;
        grid-template-columns: minmax(1px, 8fr) minmax(1px, 4fr);
        max-width: none;
    }
`;

export const HeaderContainer = styled.div`
    grid-column: 1 / -1;
`;

// CMS Property Definitions
const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(AffinityCreateAccountPage),
    definition: {
        hasEditableTitle: true,
        hasEditableUrlSegment: true,
        pageType: "Content",
        fieldDefinitions: [
            MetaKeywords,
            MetaDescription,
            OpenGraphTitle,
            OpenGraphUrl,
            OpenGraphImage,
            HideHeader,
            HideFooter,
            ExcludeFromNavigation,
            ExcludeFromSignInRequired,
            {
                name: fields.categoryFilterId,
                displayName: "Category Filter GUID",
                defaultValue: "",
                editorTemplate: "TextField",
                fieldType: "General",
                isRequired: true,
                sortOrder: 150,
            },
        ],
    },
};

export default pageModule;
export const FunctionalComponentsContext = "AffinityCreateAccountPage";
